import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useCollection, indexById } from '../../runtime/mobile/useCollection';
import { useAuth } from '../../contexts/AuthContext';
import { TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Card, ErrorBanner, GhostButton, Loader, Pill, SectionLabel, screen } from '../ui';
import { describeAudit } from './auditLabels';
import { buildHoursCsv, computePay, hoursExportFilename, saveHoursImage, saveTextFile } from './exportHours';

const rub = (n) => `${Math.round(n).toLocaleString('ru-RU')} ₽`;

const geoLabel = (g) => {
  if (!g) return null;
  if (g.withinRadius === true) return { label: 'на месте', kind: 'good' };
  if (g.withinRadius === false) {
    const d = g.distanceM;
    return { label: d >= 1000 ? `${(d / 1000).toFixed(1)} км от точки` : `${d} м от точки`, kind: 'bad' };
  }
  return { label: 'гео без радиуса', kind: 'muted' };
};

const toMs = (v) => {
  if (v == null) return NaN;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && typeof v.seconds === 'number') return v.seconds * 1000 + (v.nanoseconds || 0) / 1e6;
  return Date.parse(v);
};
const time = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const date = (ms) => { const d = new Date(ms); return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`; };
const fmtHours = (h) => {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return mm ? `${hh} ч ${mm} мин` : `${hh} ч`;
};

const PERIODS = [['week', '7 дней'], ['month', '30 дней'], ['all', 'Всё время']];
const periodCutoff = (key) => {
  if (key === 'week') return Date.now() - 7 * 864e5;
  if (key === 'month') return Date.now() - 30 * 864e5;
  return 0;
};

// Turns finalized shift reports into "hours worked per employee, split by
// position" — each assignment-history slice clipped to the shift's real
// start/end window.
function buildWorklog(reports, cutoff) {
  const byEmployee = new Map();
  for (const r of reports) {
    const finMs = toMs(r.finalizedAt || r.createdAt);
    if (!(finMs >= cutoff)) continue;
    const shift = r.shiftSnapshot || {};
    const startedMs = toMs(shift.actualStartedAt);
    const endedMs = toMs(shift.actualEndedAt) || finMs;
    if (!Number.isFinite(startedMs) || !Number.isFinite(endedMs) || endedMs <= startedMs) continue;
    const position = r.scheduleEntrySnapshot?.position || '—';
    const slices = (r.assignmentHistorySnapshot || []).filter((a) => a && a.employeeId);
    const effective = slices.length
      ? slices
      : [{ employeeId: shift.currentEmployeeId || shift.originalEmployeeId, effectiveAt: startedMs, endedAt: endedMs }];
    for (const a of effective) {
      const from = Math.max(toMs(a.effectiveAt) || startedMs, startedMs);
      const to = Math.min(a.endedAt != null ? (toMs(a.endedAt) || endedMs) : endedMs, endedMs);
      const hours = (to - from) / 36e5;
      if (!(hours > 0) || !a.employeeId) continue;
      let row = byEmployee.get(a.employeeId);
      if (!row) { row = { employeeId: a.employeeId, total: 0, shifts: 0, dayKeys: new Set(), byPosition: {}, byLocation: {} }; byEmployee.set(a.employeeId, row); }
      row.total += hours;
      row.shifts += 1;
      row.dayKeys.add(r.id || `${r.locationId}:${date(startedMs)}`);
      row.byPosition[position] = (row.byPosition[position] || 0) + hours;
      const loc = r.locationId;
      row.byLocation[loc] = (row.byLocation[loc] || 0) + hours;
    }
  }
  const out = [...byEmployee.values()];
  for (const r of out) { r.days = r.dayKeys.size; delete r.dayKeys; }
  return out.sort((a, b) => b.total - a.total);
}

function Segmented({ options, value, onChange }) {
  return (
    <View style={styles.segment}>
      {options.map(([key, label]) => (
        <Pressable key={key} onPress={() => onChange(key)} style={[styles.segItem, value === key && styles.segItemOn]}>
          <Text style={[styles.segText, value === key && styles.segTextOn]}>{label}</Text>
        </Pressable>
      ))}
    </View>
  );
}

function HoursView({ reports, users, locations }) {
  const [period, setPeriod] = useState('month');
  const nameById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.fullName || u.displayName || u.email || u.id;
    return m;
  }, [users]);
  const payRateById = useMemo(() => {
    const m = {};
    for (const u of users) if (u.payRate) m[u.id] = u.payRate;
    return m;
  }, [users]);
  const locById = indexById(locations);
  const rows = useMemo(() => buildWorklog(reports, periodCutoff(period)), [reports, period]);
  const grandTotal = rows.reduce((s, r) => s + r.total, 0);
  const grandPay = rows.reduce((s, r) => s + computePay(r, payRateById[r.employeeId]).amount, 0);
  const [exportMsg, setExportMsg] = useState(null);

  const periodLabel = (PERIODS.find(([k]) => k === period) || [])[1] || '';
  const doExport = async () => {
    setExportMsg(null);
    const csv = buildHoursCsv(rows, { nameById, locById, periodLabel, payRateById });
    const res = await saveTextFile(hoursExportFilename(period), csv);
    setExportMsg(
      res.mode === 'download' ? 'CSV сохранён — откройте в Excel или Google Таблицах.'
        : res.mode === 'clipboard' ? 'Скопировано в буфер — вставьте в таблицу (Ctrl+V).'
          : 'Не удалось выгрузить на этом устройстве.',
    );
  };
  const doImage = async () => {
    setExportMsg(null);
    const res = await saveHoursImage(rows, { nameById, periodLabel, payRateById, filename: hoursExportFilename(period, 'png') });
    setExportMsg(
      res.mode === 'download' ? 'Картинка сохранена — можно переслать бухгалтеру.'
        : 'Картинку удаётся сохранить только в браузере — используйте CSV.',
    );
  };

  return (
    <View>
      <Segmented options={PERIODS} value={period} onChange={setPeriod} />
      {!rows.length && <Text style={styles.empty}>За выбранный период смен не закрывали</Text>}
      {rows.length > 0 && (
        <Text style={styles.grand}>
          Всего: {fmtHours(grandTotal)} · {rows.length} чел. · к выплате ~{rub(grandPay)}
        </Text>
      )}
      {rows.length > 0 && (
        <View style={styles.exportWrap}>
          <GhostButton title="Выгрузить для зарплаты (CSV)" onPress={doExport} />
          <GhostButton title="Сохранить картинкой" onPress={doImage} />
          {exportMsg ? <Text style={styles.exportMsg}>{exportMsg}</Text> : null}
        </View>
      )}
      {rows.map((r) => {
        const positions = Object.entries(r.byPosition).sort((a, b) => b[1] - a[1]);
        const locs = Object.entries(r.byLocation).sort((a, b) => b[1] - a[1]);
        const pay = computePay(r, payRateById[r.employeeId]);
        return (
          <Card key={r.employeeId}>
            <View style={styles.rowTop}>
              <Text style={styles.name}>{nameById[r.employeeId] || r.employeeId}</Text>
              <Text style={styles.bigHours}>{fmtHours(r.total)}</Text>
            </View>
            <Text style={styles.meta}>
              {r.days} смен{r.days % 10 === 1 && r.days % 100 !== 11 ? 'а' : ''} · к выплате ~{rub(pay.amount)} ({pay.label}{pay.isDefault ? ', общая' : ''})
            </Text>
            <View style={styles.breakdown}>
              {positions.map(([pos, h]) => (
                <View key={pos} style={styles.brRow}>
                  <Text style={styles.brLabel}>{pos}</Text>
                  <Text style={styles.brValue}>{fmtHours(h)}</Text>
                </View>
              ))}
            </View>
            {locs.length > 1 && (
              <View style={styles.locLine}>
                {locs.map(([loc, h]) => (
                  <Pill key={loc} label={`${locById[loc]?.name || loc}: ${fmtHours(h)}`} />
                ))}
              </View>
            )}
          </Card>
        );
      })}
    </View>
  );
}

export default function ShiftReportsScreen() {
  const { user } = useAuth();
  const reports = useCollection('shiftReports');
  const audit = useCollection('auditEvents');
  const locations = useCollection('locations');
  const users = useCollection('users');
  const [tab, setTab] = useState('shifts');
  const [openId, setOpenId] = useState(null);
  const [geoByShift, setGeoByShift] = useState({});

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    (async () => {
      try {
        const token = await user.getIdToken();
        const r = await fetch(`${TEST_WORKER_URL}/checkin/geo/list`, {
          method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: '{}',
        });
        const j = await r.json().catch(() => ({}));
        if (alive && Array.isArray(j.items)) {
          setGeoByShift(Object.fromEntries(j.items.map((g) => [g.shiftId, g])));
        }
      } catch { /* optional */ }
    })();
    return () => { alive = false; };
  }, [user]);

  const locById = indexById(locations.docs);
  const auditByShift = useMemo(() => {
    const map = {};
    for (const e of audit.docs) {
      if (!e.shiftId) continue;
      (map[e.shiftId] = map[e.shiftId] || []).push(e);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));
    return map;
  }, [audit.docs]);

  const ordered = useMemo(
    () => [...reports.docs].sort((a, b) => toMs(b.finalizedAt || b.createdAt) - toMs(a.finalizedAt || a.createdAt)),
    [reports.docs],
  );

  if (reports.loading && !reports.docs.length) return <Loader />;

  return (
    <ScrollView style={screen.root} contentContainerStyle={screen.content}>
      <ErrorBanner message={reports.error} />
      <Segmented options={[['shifts', 'По сменам'], ['hours', 'Отработка']]} value={tab} onChange={setTab} />

      {tab === 'hours' && <HoursView reports={reports.docs} users={users.docs} locations={locations.docs} />}

      {tab === 'shifts' && !ordered.length && <Text style={styles.empty}>Пока нет ни одного отчёта</Text>}
      {tab === 'shifts' && ordered.map((r) => {
        const forced = r.summary?.forceClosed;
        const snaps = r.taskSnapshots || [];
        const done = snaps.filter((t) => t.status === 'completed').length;
        const open = openId === r.id;
        const events = auditByShift[r.id] || [];
        const emp = r.shiftSnapshot?.currentEmployeeId;
        const geo = geoLabel(geoByShift[r.id]);
        return (
          <Card key={r.id} onPress={() => setOpenId(open ? null : r.id)} accent={forced ? 'rgba(255,90,106,0.3)' : 'rgba(0,210,160,0.2)'}>
            <View style={styles.rowTop}>
              <View style={styles.grow}>
                <Text style={styles.title}>
                  {locById[r.locationId]?.name || r.locationId} · {date(toMs(r.finalizedAt || r.createdAt))}
                </Text>
                <Text style={styles.meta}>
                  {done}/{snaps.length} задач
                  {r.scheduleEntrySnapshot?.position ? ` · ${r.scheduleEntrySnapshot.position}` : ''}
                  {emp && usersName(users.docs, emp) ? ` · ${usersName(users.docs, emp)}` : ''}
                </Text>
              </View>
              <View style={styles.pillCol}>
                <Pill label={forced ? 'форс' : 'штатно'} kind={forced ? 'bad' : 'good'} />
                {geo ? <Pill label={geo.label} kind={geo.kind} /> : null}
              </View>
            </View>

            {open && (
              <View style={styles.detail}>
                <SectionLabel>Хронология</SectionLabel>
                {events.length === 0 && <Text style={styles.meta}>Событий аудита нет</Text>}
                {events.map((e, i) => (
                  <View key={i} style={styles.ev}>
                    <Text style={styles.evTime}>{time(toMs(e.timestamp))}</Text>
                    <View style={[styles.dot, e.type.includes('force') && styles.dotBad]} />
                    <Text style={styles.evText}>{describeAudit(e.type)}</Text>
                  </View>
                ))}
              </View>
            )}
          </Card>
        );
      })}
    </ScrollView>
  );
}

function usersName(users, id) {
  const u = users.find((x) => x.id === id);
  return u ? (u.fullName || u.displayName || '') : '';
}

const styles = StyleSheet.create({
  segment: { flexDirection: 'row', gap: spacing.xs, backgroundColor: colors.surfaceCard, borderRadius: radius.sm, padding: 3, marginBottom: spacing.md },
  segItem: { flex: 1, paddingVertical: spacing.sm, alignItems: 'center', borderRadius: radius.sm - 2 },
  segItemOn: { backgroundColor: 'rgba(0,210,160,0.14)' },
  segText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  segTextOn: { color: colors.primary },
  grand: { ...typography.caption, color: colors.textSecondary, marginBottom: spacing.sm },
  exportWrap: { marginBottom: spacing.md, gap: spacing.xs },
  exportMsg: { ...typography.caption, color: colors.textSecondary },
  rowTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  pillCol: { alignItems: 'flex-end', gap: 4 },
  grow: { flex: 1 },
  title: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14 },
  name: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 15, flex: 1 },
  bigHours: { ...typography.h3, color: colors.primary, fontFamily: typography.h3.fontFamily },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  breakdown: { marginTop: spacing.sm, gap: 3 },
  brRow: { flexDirection: 'row', justifyContent: 'space-between' },
  brLabel: { ...typography.caption, color: colors.textSecondary },
  brValue: { ...typography.caption, color: colors.textPrimary, fontVariant: ['tabular-nums'] },
  locLine: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.sm },
  detail: { marginTop: spacing.md, gap: 2 },
  ev: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 4 },
  evTime: { ...typography.caption, color: colors.textMuted, fontVariant: ['tabular-nums'], width: 42 },
  dot: { width: 7, height: 7, borderRadius: 4, backgroundColor: colors.primary },
  dotBad: { backgroundColor: colors.danger },
  evText: { ...typography.caption, color: colors.textPrimary, flex: 1 },
  empty: { ...typography.body, color: colors.textMuted, textAlign: 'center', paddingVertical: spacing.xxl },
});
